
import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import ContactSection from '../../components/feature/ContactSection';
import HubSpotForm from '../../components/common/HubSpotForm';
import { api } from '../../lib/api';
import { useContentManager } from '../../hooks/useContentManager';

type PropertyModalProps = {
  property: any;
  onClose: () => void;
  onRegister: () => void;
};

const PropertyModal = ({ property, onClose, onRegister }: PropertyModalProps) => (
  <div
    className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 cursor-pointer"
    onClick={onClose}
  >
    <div
      className="bg-white text-black rounded-lg max-w-3xl w-full p-6 cursor-default"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-semibold">{property?.title || 'Property Details'}</h3>
        <button onClick={onClose} className="text-gray-600 hover:text-black cursor-pointer">
          <i className="ri-close-line text-2xl"></i>
        </button>
      </div>
      <div className="space-y-4">
        {Array.isArray(property?.gallery) && property.gallery.length > 0 && (
          <img src={property.gallery[0]} alt="Property" className="w-full h-64 object-cover rounded" />
        )}
        <div className="flex justify-end gap-2">
          <button onClick={onRegister} className="px-4 py-2 bg-black text-white rounded cursor-pointer">Register Interest</button>
        </div>
      </div>
    </div>
  </div>
);

const CollectionsPage = () => {
  const { getContentItem, loading } = useContentManager();
  const [activeTab, setActiveTab] = useState('location');
  const [activeUnit, setActiveUnit] = useState('one-bedroom');
  const [currentGallerySlide, setCurrentGallerySlide] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPropertyModalOpen, setIsPropertyModalOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<any>(null);
  const [currentPropertyImageIndex, setCurrentPropertyImageIndex] = useState(0);

  useEffect(() => {
    if (!isModalOpen && !isPropertyModalOpen && !selectedProperty) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsModalOpen(false);
        setIsPropertyModalOpen(false);
        setSelectedProperty(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen, isPropertyModalOpen, selectedProperty]);

  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    email: '',
    unitType: ''
  });
  const [countryCode, setCountryCode] = useState('+971');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  // Admin-managed hero media for Amenities page
  const amenitiesHeroItem = getContentItem('amenities', 'hero');
  const amenitiesHeroVideo = amenitiesHeroItem?.metadata?.video_url || amenitiesHeroItem?.video_url || '';
  const amenitiesHeroPoster = amenitiesHeroItem?.metadata?.poster_url || '';

  // Hero text/button (admin-managed)
  const amenitiesHeroTextItem = getContentItem('amenities', 'hero-text');
  const heroTitleLine1 = amenitiesHeroTextItem?.metadata?.title_line1 || '';
  const heroTitleBold = amenitiesHeroTextItem?.metadata?.title_bold || '';
  const heroSubtitle = amenitiesHeroTextItem?.metadata?.subtitle || '';
  const heroButtonText = amenitiesHeroTextItem?.metadata?.button_text || '';
  const heroButtonUrl = amenitiesHeroTextItem?.metadata?.button_url || '';

  // WHY section (admin-managed)
  const amenitiesIntroItem = getContentItem('amenities', 'intro');
  const introTitleLine1 = amenitiesIntroItem?.metadata?.title_line1 || 'WHY';
  const introTitleBold = amenitiesIntroItem?.metadata?.title_bold || 'GOLDEN TOWER?';
  const introDescription = amenitiesIntroItem?.metadata?.description || '';
  const introButtonText = amenitiesIntroItem?.metadata?.button_text || 'REGISTER YOUR INTEREST';
  const introButtonUrl = amenitiesIntroItem?.metadata?.button_url || '#';

  // Amenities alternating sections (admin-managed)
  const getAmenSection = (key: 'section1' | 'section2' | 'section3' | 'section4' | 'section5' | 'section6') => {
    const item = getContentItem('amenities', key);
    const raw = Array.isArray(item?.metadata?.images) ? item!.metadata.images : [];
    const list = raw
      .map((it: any) => {
        if (typeof it === 'string') return { image: it, text: '' };
        const image = it?.image || it?.url || it?.src || '';
        const text = it?.text || it?.caption || '';
        return image ? { image, text } : null;
      })
      .filter(Boolean) as { image: string; text: string }[];
    return list;
  };
  const amenSec1 = getAmenSection('section1');
  const amenSec2 = getAmenSection('section2');
  const amenSec3 = getAmenSection('section3');
  const amenSec4 = getAmenSection('section4');
  const amenSec5 = getAmenSection('section5');
  const amenSec6 = getAmenSection('section6');
  const [amenSecIndices, setAmenSecIndices] = useState<number[]>([0, 0, 0, 0, 0, 0]);

  // Admin-driven FAQ
  const amenitiesFaqItem = getContentItem('amenities', 'faq');
  const faqItems: { question: string; answer: string }[] = Array.isArray(amenitiesFaqItem?.metadata?.faqs)
    ? amenitiesFaqItem!.metadata.faqs
    : [];
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  // Video Slider (admin-managed)
  const videoSliderItem = getContentItem('amenities', 'video-slider');
  const amenVideos: { video: string; caption?: string }[] = Array.isArray(videoSliderItem?.metadata?.videos)
    ? (videoSliderItem!.metadata.videos as any[])
        .map((it: any) => {
          if (typeof it === 'string') return { video: it };
          const video = it?.video || it?.url || '';
          const caption = it?.caption || '';
          return video ? { video, caption } : null;
        })
        .filter(Boolean) as { video: string; caption?: string }[]
    : [];
  const [amenVideoIndex, setAmenVideoIndex] = useState(0);
  const nextAmenVideo = () => {
    if (!amenVideos.length) return;
    setAmenVideoIndex((prev) => (prev + 1) % amenVideos.length);
  };
  const prevAmenVideo = () => {
    if (!amenVideos.length) return;
    setAmenVideoIndex((prev) => (prev - 1 + amenVideos.length) % amenVideos.length);
  };

  // Gallery (admin-managed, like Residences)
  const amenGalleryItem = getContentItem('amenities', 'gallery');
  const amenGallery: { image: string; caption?: string }[] = Array.isArray(amenGalleryItem?.metadata?.images)
    ? (amenGalleryItem!.metadata.images as any[])
        .map((it: any) => {
          if (typeof it === 'string') return { image: it };
          const image = it?.image || it?.url || it?.src || '';
          const caption = it?.text || it?.caption || '';
          return image ? { image, caption } : null;
        })
        .filter(Boolean) as { image: string; caption?: string }[]
    : [];
  const [amenGalleryIndex, setAmenGalleryIndex] = useState(0);
  const nextAmenGallery = () => {
    if (!amenGallery.length) return;
    setAmenGalleryIndex((prev) => (prev + 1) % amenGallery.length);
  };
  const prevAmenGallery = () => {
    if (!amenGallery.length) return;
    setAmenGalleryIndex((prev) => (prev - 1 + amenGallery.length) % amenGallery.length);
  };

  

  const galleryImages = [
    {
      category: 'BEDROOM',
      images: []
    },
    {
      category: 'EXTERIOR',
      images: []
    },
    {
      category: 'Living Room',
      images: []
    },
    {
      category: 'Lobby',
      images: []
    }
  ];

  const availableUnits = [
    {
      type: 'Studio',
      price: 'Starting AED 899,999',
      size: '450 sqft',
      image: 'https://readdy.ai/api/search-image?query=luxury%20studio%20apartment%20interior%20with%20modern%20furniture%20and%20floor-to-ceiling%20windows%2C%20premium%20residential%20photography%20with%20elegant%20design%20and%20city%20views&width=600&height=400&seq=studio1&orientation=landscape',
      gallery: [
        'https://readdy.ai/api/search-image?query=luxury%20studio%20apartment%20living%20area%20with%20modern%20furniture%20and%20floor-to-ceiling%20windows%2C%20premium%20interior%20design%20photography%20with%20elegant%20styling%20and%20city%20views&width=800&height=600&seq=studio_gal1&orientation=landscape',
        'https://readdy.ai/api/search-image?query=studio%20apartment%20kitchen%20with%20marble%20countertops%20and%20high-end%20appliances%2C%20luxury%20residential%20interior%20photography%20with%20contemporary%20design&width=800&height=600&seq=studio_gal2&orientation=landscape',
        'https://readdy.ai/api/search-image?query=studio%20apartment%20bathroom%20with%20marble%20finishes%20and%20modern%20fixtures%2C%20premium%20interior%20photography%20with%20spa-like%20atmosphere&width=800&height=600&seq=studio_gal3&orientation=landscape',
        'https://readdy.ai/api/search-image?query=studio%20apartment%20balcony%20with%20city%20skyline%20views%20and%20modern%20furnishings%2C%20luxury%20residential%20photography%20with%20urban%20backdrop&width=800&height=600&seq=studio_gal4&orientation=landscape'
      ]
    },
    {
      type: '1 Bedroom',
      price: 'Starting AED 1,569,999',
      size: '879 sqft',
      image: 'https://readdy.ai/api/search-image?query=luxury%20one%20bedroom%20apartment%20living%20room%20with%20modern%20furniture%20and%20panoramic%20windows%2C%20premium%20interior%20design%20photography%20with%20elegant%20decor&width=600&height=400&seq=1br1&orientation=landscape',
      gallery: [
        'https://readdy.ai/api/search-image?query=one%20bedroom%20apartment%20living%20room%20with%20floor-to-ceiling%20windows%20and%20modern%20furniture%2C%20luxury%20interior%20design%20photography%20with%20natural%20lighting&width=800&height=600&seq=1br_gal1&orientation=landscape',
        'https://readdy.ai/api/search-image?query=one%20bedroom%20apartment%20master%20bedroom%20with%20panoramic%20city%20views%20and%20contemporary%20furnishings%2C%20premium%20interior%20photography%20with%20soft%20lighting&width=800&height=600&seq=1br_gal2&orientation=landscape',
        'https://readdy.ai/api/search-image?query=one%20bedroom%20apartment%20kitchen%20with%20marble%20countertops%20and%20premium%20appliances%2C%20luxury%20residential%20interior%20photography&width=800&height=600&seq=1br_gal3&orientation=landscape',
        'https://readdy.ai/api/search-image?query=one%20bedroom%20apartment%20bathroom%20with%20luxury%20finishes%20and%20modern%20design%2C%20premium%20interior%20photography%20with%20elegant%20styling&width=800&height=600&seq=1br_gal4&orientation=landscape'
      ]
    },
    {
      type: '1 Bedroom Plus',
      price: 'Starting AED 1,799,999',
      size: '950 sqft',
      image: 'https://readdy.ai/api/search-image?query=luxury%20one%20bedroom%20plus%20apartment%20with%20spacious%20living%20area%20and%20modern%20design%2C%20premium%20residential%20photography%20with%20elegant%20furnishings&width=600&height=400&seq=1brplus1&orientation=landscape',
      gallery: [
        'https://readdy.ai/api/search-image?query=one%20bedroom%20plus%20apartment%20spacious%20living%20room%20with%20modern%20furniture%20and%20panoramic%20city%20views%2C%20luxury%20interior%20photography&width=800&height=600&seq=1brplus_gal1&orientation=landscape',
        'https://readdy.ai/api/search-image?query=one%20bedroom%20plus%20apartment%20master%20bedroom%20with%20walk-in%20closet%20and%20luxury%20finishes%2C%20premium%20interior%20photography&width=800&height=600&seq=1brplus_gal2&orientation=landscape',
        'https://readdy.ai/api/search-image?query=one%20bedroom%20plus%20apartment%20dining%20area%20with%20modern%20table%20and%20elegant%20lighting%2C%20luxury%20residential%20photography&width=800&height=600&seq=1brplus_gal3&orientation=landscape',
        'https://readdy.ai/api/search-image?query=one%20bedroom%20plus%20apartment%20study%20room%20with%20modern%20desk%20and%20city%20views%2C%20premium%20interior%20design%20photography&width=800&height=600&seq=1brplus_gal4&orientation=landscape'
      ]
    },
    {
      type: '2 Bedroom',
      price: 'Starting AED 2,324,999',
      size: '1067 sqft',
      image: 'https://readdy.ai/api/search-image?query=luxury%20two%20bedroom%20apartment%20living%20room%20with%20modern%20furniture%20and%20panoramic%20windows%2C%20premium%20interior%20design%20photography%20with%20elegant%20styling&width=600&height=400&seq=2br1&orientation=landscape',
      gallery: [
        'https://readdy.ai/api/search-image?query=two%20bedroom%20apartment%20spacious%20living%20room%20with%20modern%20furniture%20and%20floor-to-ceiling%20windows%2C%20luxury%20interior%20photography&width=800&height=600&seq=2br_gal1&orientation=landscape',
        'https://readdy.ai/api/search-image?query=two%20bedroom%20apartment%20master%20bedroom%20with%20panoramic%20city%20views%20and%20contemporary%20design%2C%20premium%20interior%20photography&width=800&height=600&seq=2br_gal2&orientation=landscape',
        'https://readdy.ai/api/search-image?query=two%20bedroom%20apartment%20second%20bedroom%20with%20modern%20furnishings%20and%20natural%20lighting%2C%20luxury%20residential%20photography&width=800&height=600&seq=2br_gal3&orientation=landscape',
        'https://readdy.ai/api/search-image?query=two%20bedroom%20apartment%20kitchen%20and%20dining%20area%20with%20marble%20countertops%20and%20modern%20appliances%2C%20premium%20interior%20photography&width=800&height=600&seq=2br_gal4&orientation=landscape'
      ]
    },
    {
      type: '2 Bedroom Plus',
      price: 'Starting AED 2,699,999',
      size: '1250 sqft',
      image: 'https://readdy.ai/api/search-image?query=luxury%20two%20bedroom%20plus%20apartment%20with%20spacious%20living%20area%20and%20modern%20design%2C%20premium%20residential%20photography%20with%20elegant%20furnishings&width=600&height=400&seq=2brplus1&orientation=landscape',
      gallery: [
        'https://readdy.ai/api/search-image?query=two%20bedroom%20plus%20apartment%20large%20living%20room%20with%20modern%20furniture%20and%20panoramic%20city%20views%2C%20luxury%20interior%20photography&width=800&height=600&seq=2brplus_gal1&orientation=landscape',
        'https://readdy.ai/api/search-image?query=two%20bedroom%20plus%20apartment%20master%20bedroom%20suite%20with%20walk-in%20closet%20and%20luxury%20finishes%2C%20premium%20interior%20photography&width=800&height=600&seq=2brplus_gal2&orientation=landscape',
        'https://readdy.ai/api/search-image?query=two%20bedroom%20plus%20apartment%20family%20room%20with%20modern%20entertainment%20center%20and%20comfortable%20seating%2C%20luxury%20residential%20photography&width=800&height=600&seq=2brplus_gal3&orientation=landscape',
        'https://readdy.ai/api/search-image?query=two%20bedroom%20plus%20apartment%20guest%20bedroom%20with%20modern%20design%20and%20city%20views%2C%20premium%20interior%20design%20photography&width=800&height=600&seq=2brplus_gal4&orientation=landscape'
      ]
    },
    {
      type: '3 Bedroom',
      price: 'Starting AED 3,299,999',
      size: '1523 sqft',
      image: 'https://readdy.ai/api/search-image?query=luxury%20three%20bedroom%20apartment%20living%20room%20with%20modern%20furniture%20and%20panoramic%20windows%2C%20premium%20interior%20design%20photography%20with%20elegant%20styling&width=600&height=400&seq=3br1&orientation=landscape',
      gallery: [
        'https://readdy.ai/api/search-image?query=three%20bedroom%20apartment%20spacious%20living%20and%20dining%20area%20with%20modern%20furniture%20and%20floor-to-ceiling%20windows%2C%20luxury%20interior%20photography&width=800&height=600&seq=3br_gal1&orientation=landscape',
        'https://readdy.ai/api/search-image?query=three%20bedroom%20apartment%20master%20bedroom%20suite%20with%20panoramic%20city%20views%20and%20luxury%20finishes%2C%20premium%20interior%20photography&width=800&height=600&seq=3br_gal2&orientation=landscape',
        'https://readdy.ai/api/search-image?query=three%20bedroom%20apartment%20children%20bedrooms%20with%20modern%20furnishings%20and%20natural%20lighting%2C%20luxury%20residential%20photography&width=800&height=600&seq=3br_gal3&orientation=landscape',
        'https://readdy.ai/api/search-image?query=three%20bedroom%20apartment%20kitchen%20with%20marble%20island%20and%20premium%20appliances%2C%20luxury%20interior%20design%20photography&width=800&height=600&seq=3br_gal4&orientation=landscape'
      ]
    },
    {
      type: '3 Bedroom Plus',
      price: 'Starting AED 3,899,999',
      size: '1750 sqft',
      image: 'https://readdy.ai/api/search-image?query=luxury%20three%20bedroom%20plus%20apartment%20with%20spacious%20living%20area%20and%20modern%20design%2C%20premium%20residential%20photography%20with%20elegant%20furnishings&width=600&height=400&seq=3brplus1&orientation=landscape',
      gallery: [
        'https://readdy.ai/api/search-image?query=three%20bedroom%20plus%20apartment%20grand%20living%20room%20with%20modern%20furniture%20and%20panoramic%20city%20views%2C%20luxury%20interior%20photography&width=800&height=600&seq=3brplus_gal1&orientation=landscape',
        'https://readdy.ai/api/search-image?query=three%20bedroom%20plus%20apartment%20master%20bedroom%20suite%20with%20walk-in%20closet%20and%20luxury%20bathroom%2C%20premium%20interior%20photography&width=800&height=600&seq=3brplus_gal2&orientation=landscape',
        'https://readdy.ai/api/search-image?query=three%20bedroom%20plus%20apartment%20family%20room%20and%20study%20area%20with%20modern%20design%20and%20city%20views%2C%20luxury%20residential%20photography&width=800&height=600&seq=3brplus_gal3&orientation=landscape',
        'https://readdy.ai/api/search-image?query=three%20bedroom%20plus%20apartment%20maid%20room%20and%20laundry%20area%20with%20modern%20fixtures%20and%20storage%2C%20premium%20interior%20photography&width=800&height=600&seq=3brplus_gal4&orientation=landscape'
      ]
    },
    {
      type: 'Penthouse',
      price: 'Starting AED 5,999,999',
      size: '2500 sqft',
      image: 'https://readdy.ai/api/search-image?query=luxury%20penthouse%20apartment%20living%20room%20with%20modern%20furniture%20and%20panoramic%20city%20views%2C%20premium%20interior%20design%20photography%20with%20elegant%20styling&width=600&height=400&seq=penthouse1&orientation=landscape',
      gallery: [
        'https://readdy.ai/api/search-image?query=penthouse%20apartment%20grand%20living%20room%20with%20floor-to-ceiling%20windows%20and%20luxury%20furniture%2C%20premium%20interior%20photography%20with%20city%20skyline%20views&width=800&height=600&seq=penthouse_gal1&orientation=landscape',
        'https://readdy.ai/api/search-image?query=penthouse%20apartment%20master%20bedroom%20suite%20with%20panoramic%20views%20and%20luxury%20finishes%2C%20premium%20interior%20design%20photography&width=800&height=600&seq=penthouse_gal2&orientation=landscape',
        'https://readdy.ai/api/search-image?query=penthouse%20apartment%20private%20terrace%20with%20infinity%20pool%20and%20city%20views%2C%20luxury%20residential%20photography%20with%20outdoor%20furniture&width=800&height=600&seq=penthouse_gal3&orientation=landscape',
        'https://readdy.ai/api/search-image?query=penthouse%20apartment%20gourmet%20kitchen%20with%20marble%20island%20and%20premium%20appliances%2C%20luxury%20interior%20photography%20with%20elegant%20design&width=800&height=600&seq=penthouse_gal4&orientation=landscape'
      ]
    }
  ];

  

  const handleModalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // API'ye kaydet
      await api.post('/forms', {
        form_type: 'register_interest',
        name: formData.name,
        mobile: `${countryCode}${formData.mobile}`,
        email: formData.email,
        unit_type: formData.unitType,
        country_code: countryCode,
        status: 'new'
      });

      setSubmitStatus('success');
      setFormData({ name: '', mobile: '', email: '', unitType: '' });
      setTimeout(() => {
        setIsModalOpen(false);
        setSubmitStatus(null);
      }, 2000);
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const openPropertyModal = (unit: any) => {
    setSelectedProperty(unit);
    setCurrentPropertyImageIndex(0);
    setIsPropertyModalOpen(true);
  };

  const nextPropertyImage = () => {
    if (selectedProperty) {
      setCurrentPropertyImageIndex((prev) => 
        (prev + 1) % selectedProperty.gallery.length
      );
    }
  };

  const prevPropertyImage = () => {
    if (selectedProperty) {
      setCurrentPropertyImageIndex((prev) => 
        (prev - 1 + selectedProperty.gallery.length) % selectedProperty.gallery.length
      );
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Header />
      
      {/* Hero Banner (admin-managed) */}
      <section className="relative h-screen overflow-hidden">
        <div className="absolute inset-0">
          {!loading && amenitiesHeroVideo && (
            <video
              src={amenitiesHeroVideo}
              poster={amenitiesHeroPoster || undefined}
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-full object-cover"
            />
          )}
          <div className="absolute inset-0 bg-black/30"></div>
        </div>
        
        <div className="relative z-10 flex flex-col justify-center items-center h-full text-center px-4">
          {(heroTitleLine1 || heroTitleBold) && (
            <h1 className="text-5xl lg:text-7xl font-light tracking-wide text-white mb-4">
              {heroTitleLine1} {heroTitleBold && <span className="font-bold">{heroTitleBold}</span>}
            </h1>
          )}
          {heroSubtitle?.trim() && (
            <p className="max-w-3xl mx-auto text-base md:text-lg text-white/80 mb-6">{heroSubtitle}</p>
          )}
          {heroButtonText?.trim() && (
            heroButtonUrl && heroButtonUrl.trim() !== '#' ? (
              <a
                href={heroButtonUrl}
                className="border border-white px-8 py-3 text-sm tracking-wider hover:bg-white hover:text-black transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                {heroButtonText.trim()}
              </a>
            ) : (
              <button 
                onClick={() => setIsModalOpen(true)}
                className="border border-white px-8 py-3 text-sm tracking-wider hover:bg-white hover:text-black transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                {heroButtonText.trim()}
              </button>
            )
          )}
        </div>
      </section>

      

      {/* Why Golden Tower (admin-managed) */}
      <section className="py-20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-light mb-8">
            {introTitleLine1} <span className="font-bold">{introTitleBold}</span>
          </h2>
          {introDescription && (
            <p className="text-gray-300 max-w-4xl mx-auto mb-12 leading-relaxed">{introDescription}</p>
          )}
          {introButtonUrl && introButtonUrl !== '#' ? (
            <a 
              href={introButtonUrl}
              className="border border-white px-8 py-3 text-sm tracking-wider hover:bg-white hover:text-black transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              {introButtonText}
            </a>
          ) : (
            <button 
              onClick={() => setIsModalOpen(true)}
              className="border border-white px-8 py-3 text-sm tracking-wider hover:bg-white hover:text-black transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              {introButtonText}
            </button>
          )}
        </div>
      </section>

      {/* Amenities Sections (4 sections, alternating image side) with sliders */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4 space-y-16">
          {[amenSec1, amenSec2, amenSec3, amenSec4, amenSec5, amenSec6].map((sec, idx) => {
            if (!sec || sec.length === 0) return null;
            const active = amenSecIndices[idx] ?? 0;
            const len = sec.length;
            const leftOrder = idx % 2 === 1 ? 'lg:order-2' : '';
            const rightOrder = idx % 2 === 1 ? 'lg:order-1' : '';
            return (
              <div key={idx} className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div className={`text-center lg:text-left ${leftOrder}`}>
                  {sec[active]?.text && (
                    <h3 className="text-3xl font-light mb-6">{sec[active].text}</h3>
                  )}
                  {/* Dots */}
                  {len > 1 && (
                    <div className="flex justify-center lg:justify-start space-x-2 mt-4">
                      {sec.map((_, dotIdx) => (
                        <button
                          key={dotIdx}
                          className={`w-2.5 h-2.5 rounded-full ${dotIdx === active ? 'bg-white' : 'bg-gray-600'}`}
                          onClick={() =>
                            setAmenSecIndices((prev: number[]) => {
                              const arr = [...prev];
                              arr[idx] = dotIdx;
                              return arr;
                            })
                          }
                          aria-label={`Go to slide ${dotIdx + 1}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
                <div className={`relative ${rightOrder}`}>
                  <div className="relative h-96 rounded-lg overflow-hidden">
                    <img
                      src={sec[active].image}
                      alt={`Amenities Section ${idx + 1} Slide ${active + 1}`}
                      className="w-full h-full object-cover"
                    />
                    {len > 1 && (
                      <>
                        <button
                          onClick={() =>
                            setAmenSecIndices((prev: number[]) => {
                              const arr = [...prev];
                              arr[idx] = (active - 1 + len) % len;
                              return arr;
                            })
                          }
                          className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70"
                          aria-label="Previous slide"
                        >
                          <i className="ri-arrow-left-line text-xl"></i>
                        </button>
                        <button
                          onClick={() =>
                            setAmenSecIndices((prev: number[]) => {
                              const arr = [...prev];
                              arr[idx] = (active + 1) % len;
                              return arr;
                            })
                          }
                          className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70"
                          aria-label="Next slide"
                        >
                          <i className="ri-arrow-right-line text-xl"></i>
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>



      {/* Video Slider (admin-managed) */}
      <section className="relative overflow-hidden">
        {amenVideos.length > 0 ? (
          <div className="relative">
            <div className="relative bg-black" style={{ paddingTop: '56.25%' }}>
              <video
                key={`amen-video-${amenVideoIndex}`}
                src={amenVideos[amenVideoIndex].video}
                className="absolute inset-0 w-full h-full object-cover"
                autoPlay
                muted
                loop
                playsInline
              />
              {amenVideos[amenVideoIndex].caption && (
                <div className="absolute bottom-4 left-4 bg-black/70 text-white px-3 py-1 rounded text-xs">
                  {amenVideos[amenVideoIndex].caption}
                </div>
              )}
            </div>
            {amenVideos.length > 1 && (
              <>
                <button
                  onClick={prevAmenVideo}
                  className="absolute left-4 sm:left-6 md:left-8 lg:left-10 top-1/2 -translate-y-1/2 bg-black/50 text-white p-3 rounded-full hover:bg-black/70 z-10"
                >
                  <i className="ri-arrow-left-line text-xl"></i>
                </button>
                <button
                  onClick={nextAmenVideo}
                  className="absolute right-4 sm:right-6 md:right-8 lg:right-10 top-1/2 -translate-y-1/2 bg-black/50 text-white p-3 rounded-full hover:bg-black/70 z-10"
                >
                  <i className="ri-arrow-right-line text-xl"></i>
                </button>
                <div className="flex justify-center space-x-2 mt-6">
                  {amenVideos.map((_, i) => (
                    <button
                      key={i}
                      className={`w-2.5 h-2.5 rounded-full ${i === amenVideoIndex ? 'bg-white' : 'bg-gray-600'}`}
                      onClick={() => setAmenVideoIndex(i)}
                      aria-label={`Go to video ${i + 1}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="w-full h-[400px] bg-gray-800 flex items-center justify-center text-gray-400">
            Video list is not set
          </div>
        )}
      </section>

      {/* Gallery (admin-managed) */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-light text-center mb-10">Gallery</h2>
          {amenGallery.length > 0 ? (
            <div className="relative max-w-6xl mx-auto">
              <div className="relative rounded-lg overflow-hidden bg-black" style={{ paddingTop: '56.25%' }}>
                <img
                  src={amenGallery[amenGalleryIndex].image}
                  alt={amenGallery[amenGalleryIndex].caption || 'Gallery'}
                  className="absolute inset-0 w-full h-full object-contain"
                />
                {amenGallery[amenGalleryIndex].caption && (
                  <div className="absolute bottom-4 left-4 bg-black/70 text-white px-3 py-1 rounded text-xs tracking-widest uppercase">
                    {amenGallery[amenGalleryIndex].caption}
                  </div>
                )}
              </div>
              {amenGallery.length > 1 && (
                <>
                  <button
                    onClick={prevAmenGallery}
                    className="absolute left-4 sm:left-6 md:left-8 lg:left-10 top-1/2 -translate-y-1/2 bg-black/50 text-white p-3 rounded-full hover:bg-black/70 z-10"
                  >
                    <i className="ri-arrow-left-line text-xl"></i>
                  </button>
                  <button
                    onClick={nextAmenGallery}
                    className="absolute right-16 sm:right-20 md:right-24 lg:right-28 xl:right-32 top-1/2 -translate-y-1/2 bg-black/50 text-white p-3 rounded-full hover:bg-black/70 z-10"
                  >
                    <i className="ri-arrow-right-line text-xl"></i>
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="w-full h-[600px] rounded-lg bg-gray-800 flex items-center justify-center text-gray-400">
              GALLERY images are not set
            </div>
          )}
        </div>
      </section>

      {/* FAQ Section (admin-managed) */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-light text-center mb-12">
            <span className="font-bold">Frequently Asked</span> Questions
          </h2>
          <div className="max-w-4xl mx-auto space-y-4">
            {faqItems.map((faq, index) => (
              <div key={index} className="border-b border-gray-700">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full py-6 text-left flex justify-between items-center hover:text-gray-300 transition-colors duration-300"
                >
                  <span className="text-lg font-light">{faq.question}</span>
                  <i className={`ri-arrow-${openFaq === index ? 'up' : 'down'}-line text-xl`}></i>
                </button>
                {openFaq === index && (
                  <div className="pb-6 text-gray-300 leading-relaxed">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section (amenities-style) */}
      <ContactSection page="collections" showFloatingIcons={true} />

      {/* Modal */}
      {isModalOpen && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setIsModalOpen(false)}
        >
          <div
            className="bg-black border border-gray-700 rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto cursor-default"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex justify-between items-center p-6 border-b border-gray-700">
              <h3 className="text-xl font-light text-white">REGISTER NOW FOR <span className="font-bold">MORE DETAILS</span></h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-white transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              <HubSpotForm />
            </div>
          </div>
        </div>
      )}

      {/* Property Modal */}
      {isPropertyModalOpen && selectedProperty && (
        <div
          className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setIsPropertyModalOpen(false)}
        >
          <div
            className="bg-black border border-gray-700 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto cursor-default"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex justify-between items-center p-6 border-b border-gray-700">
              <h3 className="text-2xl font-light text-white">
                {selectedProperty.type} <span className="font-bold">Gallery</span>
              </h3>
              <button
                onClick={() => setIsPropertyModalOpen(false)}
                className="text-gray-400 hover:text-white transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {/* Property Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div>
                  <h4 className="text-xl font-light mb-4 text-white">{selectedProperty.type}</h4>
                  <div className="space-y-3">
                    <div className="flex items-center text-gray-300">
                      <img src="" alt="payment" className="w-5 h-5 mr-3" />
                      <span>{selectedProperty.price}</span>
                    </div>
                    <div className="flex items-center text-gray-300">
                      <img src="" alt="square" className="w-5 h-5 mr-3" />
                      <span>{selectedProperty.size}</span>
                    </div>
                  </div>
                </div>
                <div>
                  <button 
                    onClick={() => {
                      setIsPropertyModalOpen(false);
                      setIsModalOpen(true);
                      // Map property type to allowed unit types
                      const raw = (selectedProperty.type || '').toString().toLowerCase();
                      let mapped: string = '';
                      if (raw.includes('studio')) mapped = 'studio';
                      else if (raw.includes('penthouse')) mapped = 'penthouse';
                      else if (raw.includes('1') && raw.includes('bedroom')) mapped = '1bedroom';
                      else if (raw.includes('2') && raw.includes('bedroom')) mapped = '2bedroom';
                      // For unsupported types (e.g., 3 Bedroom or Plus), leave empty
                      setFormData((prev) => ({ ...prev, unitType: mapped }));
                    }}
                    className="w-full border border-white py-3 text-sm tracking-wider hover:bg-white hover:text-black transition-all duration-300 cursor-pointer whitespace-nowrap"
                  >
                    REGISTER INTEREST
                  </button>
                </div>
              </div>

              {/* Image Gallery */}
              <div className="relative">
                <div className="relative h-[500px] rounded-lg overflow-hidden">
                  <img
                    src={selectedProperty.gallery[currentPropertyImageIndex]}
                    alt={`${selectedProperty.type} ${currentPropertyImageIndex + 1}`}
                    className="w-full h-full object-cover"
                  />
                  
                  {/* Navigation Arrows */}
                  <button
                    onClick={prevPropertyImage}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-3 rounded-full hover:bg-black/70 transition-all duration-300"
                  >
                    <i className="ri-arrow-left-line text-xl"></i>
                  </button>
                  <button
                    onClick={nextPropertyImage}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-3 rounded-full hover:bg-black/70 transition-all duration-300"
                  >
                    <i className="ri-arrow-right-line text-xl"></i>
                  </button>

                  {/* Image Counter */}
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/70 px-4 py-2 rounded-full">
                    <span className="text-white text-sm">
                      {currentPropertyImageIndex + 1} / {selectedProperty.gallery.length}
                    </span>
                  </div>
                </div>

                {/* Thumbnail Navigation */}
                <div className="flex justify-center mt-6 space-x-2">
                  {selectedProperty.gallery.map((image: string, index: number) => (
                    <button
                      key={index}
                      onClick={() => setCurrentPropertyImageIndex(index)}
                      className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-all duration-300 ${
                        index === currentPropertyImageIndex 
                          ? 'border-white' 
                          : 'border-gray-600 hover:border-gray-400'
                      }`}
                    >
                      <img
                        src={image}
                        alt={`Thumbnail ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Property Details Modal */}
      {selectedProperty && (
        <PropertyModal
          property={selectedProperty}
          onClose={() => setSelectedProperty(null)}
          onRegister={() => {
            setSelectedProperty(null);
            setIsModalOpen(true);
          }}
        />
      )}
      
      {/* Right Side Floating Contact Icons */}
      <div className="fixed right-6 bottom-32 z-50 flex flex-col gap-4">
        <a
          href="tel:+971123456789"
          className="w-12 h-12 rounded-full bg-black border border-white/20 hover:bg-[#C5A46D] hover:border-[#C5A46D] flex items-center justify-center transition-all duration-300 group cursor-pointer"
          title="Call Us"
        >
          <i className="ri-phone-fill text-white text-xl"></i>
        </a>
        <a
          href="https://wa.me/971123456789"
          target="_blank"
          rel="noopener noreferrer"
          className="w-12 h-12 rounded-full bg-black border border-white/20 hover:bg-[#25D366] hover:border-[#25D366] flex items-center justify-center transition-all duration-300 group cursor-pointer"
          title="WhatsApp"
        >
          <i className="ri-whatsapp-fill text-white text-xl"></i>
        </a>
        <a
          href="mailto:info@goldentower.com"
          className="w-12 h-12 rounded-full bg-black border border-white/20 hover:bg-[#C5A46D] hover:border-[#C5A46D] flex items-center justify-center transition-all duration-300 group cursor-pointer"
          title="Email Us"
        >
          <i className="ri-mail-fill text-white text-xl"></i>
        </a>
        <button
          onClick={() => {
            const link = document.createElement('a');
            link.href = '/catalog.pdf';
            link.download = 'luxury-properties-catalog.pdf';
            link.click();
          }}
          className="w-12 h-12 rounded-full bg-black border border-white/20 hover:bg-[#C5A46D] hover:border-[#C5A46D] flex items-center justify-center transition-all duration-300 group cursor-pointer"
          title="Download Catalog"
        >
          <i className="ri-file-pdf-fill text-white text-xl"></i>
        </button>
      </div>
    </div>
  );
};

export default CollectionsPage;
